import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  CSVFilesListViewResponse,
  GetTestByIdResponse,
  GetTestByIdResponseModified,
  GetTestsFromCycleResponse,
  GetTestsFromCycleResponseModified,
  InitiateCSVFilesGenerationResponse
} from '../../types';
import { CreateTestPayload, TestByIdMeters } from '../../types/records/tests';

export const testsEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'satApi'
  >
) => ({
  getTestsFromCycle: builder.query<GetTestsFromCycleResponseModified, string>({
    query: (param) => ({
      url: `api/v2/tests?testCycleId=${param}`
    }),
    providesTags: ['getTestsFromCycle'],
    transformResponse: (
      response: GetTestsFromCycleResponse
    ): GetTestsFromCycleResponseModified => {
      const newData = response.map((ele) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { commandInfo, deviceInfo, result, ...rest } = ele;
        const newObject = {
          ...rest,
          commandName: commandInfo.command,
          identifierType: deviceInfo.identifierType
        };
        return newObject;
      });
      return newData;
    }
  }),
  getTestFromId: builder.query<GetTestByIdResponseModified, string>({
    query: (param) => ({
      url: `api/v2/tests/${param}`
    }),
    transformResponse: (
      response: GetTestByIdResponse
    ): GetTestByIdResponseModified => {
      const { deviceInfo } = response;

      const modifiedResponse = response.meters.map((e) => {
        const { deviceIdentifier, deviceSerial, ...rest } = e;

        if (deviceInfo.identifierType === 'deviceIdentifier') {
          return { ...rest, deviceIdentifier };
        }

        if (deviceInfo.identifierType === 'deviceSerial') {
          return { ...rest, deviceSerial };
        }

        return e;
      });

      return { ...response, meters: modifiedResponse };
    }
  }),

  downloadTestReport: builder.query<string, string>({
    query: (param) => ({
      url: `api/v2/tests/report/download/${param}`,
      responseHandler: (response) => {
        if (response.status === 400) {
          return response.json();
        }
        return response.text();
      }
    })
  }),
  initiateCSVFilesGeneration: builder.query<
    InitiateCSVFilesGenerationResponse,
    string
  >({
    query: (testId) => ({
      url: `/api/v3/tests/response/files/init?testId=${testId}`
    })
  }),
  getCSVFilesListView: builder.query<CSVFilesListViewResponse, string>({
    query: (testId) => ({
      url: `/api/v3/tests/response/files?testId=${testId}`
    })
  }),
  createTest: builder.mutation<'', CreateTestPayload>({
    query: (data) => ({
      url: 'api/v2/tests',
      body: data,
      method: 'POST'
    }),
    invalidatesTags: ['getTestsFromCycle']
  }),
  executeTest: builder.query<'', string>({
    query: (param) => ({
      url: `api/v2/tests/execute/${param}`
    })
  }),
  getTestMeterById: builder.query<
    TestByIdMeters[] | TestByIdMeters,
    { id?: string; testId?: string }
  >({
    query: (params) => ({
      url: `api/v2/tests/meters`,
      params: params
    }),
    keepUnusedDataFor: 0
  })
});
